import React, { useState, useEffect } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { handleErrors } from "../../utils/errorHandler";
import bankDetailsServices from "../../services/owner-services/owner-bank";
import { validateBankDetails } from "../../utils/validation/owner-bank-validation";
import { FaEdit, FaTrash } from "react-icons/fa";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

const OwnerBankDetails = () => {
  const [formData, setFormData] = useState({
    bankName: "",
    accountNo: "",
    ifscCode: "",
    accountName: "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState(null);

  // 🔹 Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);

  async function fetchBankDetails() {
    try {
      const response = await bankDetailsServices.getBankDetails();
      if (response.data?.result) {
        setBankList(response.data.result);
      }
    } catch (error) {
      toast.error(handleErrors(error));
    }
  }

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async () => {
    const formErrors = validateBankDetails(formData);
    setErrors(formErrors);

    if (Object.keys(formErrors).length === 0) {
      try {
        setIsSaving(true);
        setIsButtonDisabled(true);

        if (isUpdating && selectedBankId) {
          await bankDetailsServices.updateBankDetails({
            ...formData,
            ownerId: selectedBankId,
          });
          toast.success("Bank details updated successfully!");
        } else {
          await bankDetailsServices.postBankDetails(formData);
          toast.success("Bank details added successfully!");
        }

        resetForm();
        fetchBankDetails();
      } catch (error) {
        toast.error(handleErrors(error));
      } finally {
        setIsSaving(false);
        setIsButtonDisabled(false);
      }
    }
  };

  const handleEdit = async (bank) => {
    try {
      const response = await bankDetailsServices.getBankIDDetails(bank.id);
      if (response.data?.result) {
        const data = response.data.result;
        setFormData({
          bankName: data.bankName || "",
          accountNo: data.accountNo || "",
          ifscCode: data.ifscCode || "",
          accountName: data.accountName || "",
        });
        setSelectedBankId(bank.id);
        setIsUpdating(true);
      }
    } catch (error) {
      toast.error(handleErrors(error));
    }
  };

  const resetForm = () => {
    setFormData({
      bankName: "",
      accountNo: "",
      ifscCode: "",
      accountName: "",
    });
    setIsUpdating(false);
    setSelectedBankId(null);
  };

  const confirmDelete = (id) => {
    setBankToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    try {
      await bankDetailsServices.deleteBank(bankToDelete);
      toast.success("Bank detail deleted successfully!");
      fetchBankDetails();
    } catch (error) {
      toast.error(handleErrors(error));
    } finally {
      setDeleteDialogOpen(false);
      setBankToDelete(null);
    }
  };
  const handleMarkPrimary = async (bankId) => {
    try {
      await bankDetailsServices.markPrimary(bankId);
      toast.success("Bank marked as primary successfully!");

      // Update UI immediately (no need to refetch if you prefer)
      setBankList((prev) =>
        prev.map((b) => ({
          ...b,
          isPrimary: b.id === bankId ? "Yes" : "No",
        }))
      );
    } catch (error) {
      toast.error(handleErrors(error));
    }
  };

  return (
    <div className="personal-info">
      <div className="my-account-title">
        <h3>Bank Details</h3>
      </div>

      {/* Bank Details Form */}
      <div className="profile-info-wrap">
        <Row>
          <Col xs={12} xl={4} lg={6} sm={6}>
            <Form.Group className="form-group">
              <Form.Label>Bank Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Bank Name*"
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                isInvalid={!!errors.bankName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.bankName}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12} xl={4} lg={6} sm={6}>
            <Form.Label>Account Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="Account Number*"
              name="accountNo"
              value={formData.accountNo}
              onChange={handleInputChange}
              isInvalid={!!errors.accountNo}
            />
            <Form.Control.Feedback type="invalid">
              {errors.accountNo}
            </Form.Control.Feedback>
          </Col>
          <Col xs={12} xl={4} lg={6} sm={6}>
            <Form.Label>IFSC Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="IFSC Code*"
              name="ifscCode"
              value={formData.ifscCode}
              onChange={handleInputChange}
              isInvalid={!!errors.ifscCode}
            />
            <Form.Control.Feedback type="invalid">
              {errors.ifscCode}
            </Form.Control.Feedback>
          </Col>
          <Col xs={12} xl={4} lg={6} sm={6}>
            <Form.Label>Account Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Account Name*"
              name="accountName"
              value={formData.accountName}
              onChange={handleInputChange}
              isInvalid={!!errors.accountName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.accountName}
            </Form.Control.Feedback>
          </Col>
        </Row>
      </div>

      <div className="save-btn mt-2">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isButtonDisabled}
          className="me-2"
        >
          {isSaving
            ? "Saving..."
            : isUpdating
            ? "Update Bank Details"
            : "Save Bank Details"}
        </Button>

        {isUpdating && (
          <Button variant="secondary" onClick={resetForm}>
            Cancel
          </Button>
        )}
      </div>

      {/* Table for displaying bank details */}
      <div className="mt-4">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Bank Name</th>
              <th>Account No</th>
              <th>IFSC Code</th>
              <th>Account Holder</th>
              <th>Primary Account</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bankList.length > 0 ? (
              bankList.map((bank) => (
                <tr key={bank.id}>
                  <td>{bank.bankName}</td>
                  <td>{bank.accountNo}</td>
                  <td>{bank.ifscCode}</td>
                  <td>{bank.accountName}</td>

                  <td className="text-center">
                    <input
                      type="radio"
                      name="primaryBank"
                      checked={
                        bank.isPrimary === "Yes" || bank.isPrimary === true
                      }
                      onChange={() => handleMarkPrimary(bank.id)}
                    />
                    {bank.isPrimary === "Yes" && <></>}
                  </td>

                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(bank)}
                      className="me-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => confirmDelete(bank.id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="text-center">
                  No bank details found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* MUI Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{"Delete Bank Detail"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this bank detail? This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => setDeleteDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OwnerBankDetails;
